import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiGrid, FiArrowRight, FiList, FiColumns, FiCalendar,
  FiCheckCircle, FiLayout
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const ViewsBoards = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/features/views" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/features" className="hover:text-blue-600">Features</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Views & Boards</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl mb-4">
              <FiGrid className="w-6 h-6 text-indigo-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Views & Boards</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Visualize your work in different ways with multiple view options for tasks and projects.
            </p>
          </div>

          {/* Overview */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Multiple Perspectives</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Different situations call for different views. Whether you prefer seeing tasks in a list, 
                on a Kanban board, or in a calendar, Tasklina lets you switch between views instantly 
                while keeping all your data synchronized.
              </p>
            </div>
          </section>

          {/* View Types */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Available Views</h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <FiList className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">List View</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  A traditional list format that shows all tasks with their details in rows. 
                  Perfect for quickly scanning through many tasks.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-4">
                  <li>• Sort by any field (due date, priority, assignee)</li>
                  <li>• Quick inline editing</li>
                  <li>• Filter and group tasks</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <FiColumns className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Board View (Kanban)</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  Visual Kanban board where tasks are organized in columns by status. 
                  Drag and drop to move tasks between stages.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-4">
                  <li>• Drag-and-drop between columns</li>
                  <li>• Custom column configurations</li>
                  <li>• Work-in-progress limits</li>
                </ul>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <FiCalendar className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Calendar View</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-3">
                  See tasks plotted on a calendar based on their due dates. 
                  Great for deadline management and planning.
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 ml-4">
                  <li>• Month, week, and day views</li>
                  <li>• Drag to reschedule tasks</li>
                  <li>• Color-coded by project or priority</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Switching Views */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Switching Between Views</h2>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
              <ol className="space-y-3 text-gray-600 dark:text-gray-300">
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                  <span>Navigate to any project or task list</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                  <span>Look for the view switcher icons in the top toolbar</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                  <span>Click on your preferred view type</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">4</span>
                  <span>Your selection is saved per project</span>
                </li>
              </ol>
            </div>
          </section>

          {/* Tips */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Pro Tips</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Use Board for Active Work</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Kanban boards work best for tracking work in progress</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Calendar for Deadlines</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Use calendar view when managing time-sensitive tasks</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">List for Review</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">List view is ideal for reviewing and bulk-editing tasks</p>
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <FiCheckCircle className="w-5 h-5 text-green-500 mb-2" />
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Save Custom Views</h4>
                <p className="text-sm text-gray-600 dark:text-gray-300">Create saved views with specific filters for quick access</p>
              </div>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Features</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/features/calendar"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Calendar Integration</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/personal/tasks"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Task Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">Last updated: January 27, 2026</div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">Yes</button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700">No</button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default ViewsBoards;
